import { useEffect, useState } from "react";
import { Loader2, Download, FileWarning } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function isPreviewable(contentType) {
  return contentType?.startsWith("image/") || contentType === "application/pdf";
}

export default function FilePreviewModal({ submissionId, file, onClose }) {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    let url = null;
    setLoading(true);
    api.get(`/admin/documents/${submissionId}/files/${file.id}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load preview");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [submissionId, file]);

  function download() {
    if (!blobUrl || !file) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file.original_name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] bg-[var(--surface)] border border-white/10 flex flex-col" data-testid="admin-file-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-base truncate">{file?.original_name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-[300px] overflow-auto grid place-items-center bg-black/30 rounded-lg">
          {loading && <Loader2 size={28} className="animate-spin text-[var(--gold)]" />}
          {!loading && blobUrl && file && isPreviewable(file.content_type) && file.content_type.startsWith("image/") && (
            <img src={blobUrl} alt={file.original_name} className="max-w-full max-h-[65vh] object-contain" />
          )}
          {!loading && blobUrl && file && file.content_type === "application/pdf" && (
            <embed src={blobUrl} type="application/pdf" className="w-full h-[65vh]" />
          )}
          {!loading && file && !isPreviewable(file.content_type) && (
            <div className="text-center text-white/50 p-8">
              <FileWarning size={28} className="mx-auto mb-2 text-white/30" />
              <p className="text-sm">No inline preview available for this file type.</p>
              <p className="text-xs text-white/35 mt-1">Use Download instead.</p>
            </div>
          )}
        </div>

        <button onClick={download} disabled={!blobUrl} className="btn-gold text-sm py-2 px-5 self-start disabled:opacity-40" data-testid="admin-file-preview-download">
          <Download size={14} /> Download
        </button>
      </DialogContent>
    </Dialog>
  );
}
